import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { Input } from "@krea/shared/design-system/input/input";
import { Label } from "@krea/shared/design-system/Label";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Separator } from "@krea/shared/design-system/Separator";
import { Switch } from "@krea/shared/design-system/Switch";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";
import { LenderProfileSectionCard } from "./lender-profile-section-card";

interface ILenderCompanyFeatureWrapperProps {
  feature: boolean;
  title: string;
  description: string;
  disabled?: boolean;
}

const LenderCompanyFeatureWrapper = ({
  feature,
  title,
  description,
  disabled,
}: ILenderCompanyFeatureWrapperProps) => {
  return (
    <div className="col-span-2 flex items-center justify-between mb-4">
      <div>
        <Label>{title}</Label>

        <p className="text-base-strong">{description}</p>
      </div>
      {!disabled ? (
        <div>
          <Switch checked={feature} />
        </div>
      ) : (
        <ResponsiveTooltip
          trigger={
            <div>
              <Switch checked={feature} />
            </div>
          }
          interactive
        >
          To {feature ? "disable" : "enable"} this feature, please contact
          customer support.
        </ResponsiveTooltip>
      )}
    </div>
  );
};
const VALID_VARIABLES = [
  {
    variable: "{lender_application_id}",
    description:
      'Id which is unique per each customers application and lender. This is your "main" ID to use',
  },
  {
    variable: "{lender_response_id}",
    description: "Id which is unique per lender response",
  },
  {
    variable: "{loan_application_id}",
    description: "Id which is unique for each customers application",
  },
];

const schema = yup.object().shape({
  signingUrlTemplate: yup.string().nonNullable(),
});

type IFormValues = yup.InferType<typeof schema>;

export const LenderKreaFeatures = () => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const _queryClient = useQueryClient();
  const form = useForm<IFormValues>({
    mode: "onChange",
    defaultValues: {
      signingUrlTemplate: lenderCompany?.signingUrlTemplate ?? "",
    },
  });
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  useEffect(() => {
    if (lenderCompany) {
      const initialValues: IFormValues = {
        signingUrlTemplate: lenderCompany.signingUrlTemplate ?? "",
      };
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;
    form.handleSubmit((data) => {
      updateLenderCompany({ lenderCompanyId: lenderCompany.id, data });
    })();
  }, [lenderCompany, form, updateLenderCompany]);

  return (
    <LenderProfileSectionCard
      title="Krea Features"
      description="Enable or disable Krea features for the company"
    >
      <LenderCompanyFeatureWrapper
        feature={!!lenderCompany?.isFeatureOfferAttractivenessActivated}
        title="Offer Attractiveness feature"
        description="Enable the Offer Attractiveness feature to view your offers' attractiveness when submitting offers."
        disabled
      />
      <LenderCompanyFeatureWrapper
        feature={!!lenderCompany?.isFeatureCreditTermsFileActivated}
        title="Credit Terms File feature"
        description="Enable the Credit Terms File feature to upload credit terms files when submitting offers."
        disabled
      />
      <LenderCompanyFeatureWrapper
        feature={!!lenderCompany?.isFeatureInterestRatePerYearActivated}
        title="Interest Rate Per Year feature"
        description="Enable the Interest Rate Per Year feature to view your offers' interest rate per year when submitting offers."
        disabled
      />

      <div className="space-y-6">
        <div>
          <ControlledWrapper
            control={form.control}
            name="signingUrlTemplate"
            label="Your link template"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="wonCustomerLinkTemplate"
                  placeholder="https://your-company.com/success?id={lender_application_id}"
                  className="text-gray-600"
                  value={field.value}
                  onChange={field.onChange}
                />
                {!fieldState.invalid && (
                  <p className="text-[0.8rem] text-gray-600">
                    The link template for customers who accept your offer.
                  </p>
                )}
              </>
            )}
          />
        </div>

        {/* Use case examples */}
        <div className="space-y-3">
          <h3 className="text-base-strong">Use case examples</h3>

          <div className="rounded-md border border-gray-200 bg-gray-100 p-3">
            <p className="mb-1 text-helper">Sign a debt letter:</p>
            <code className="block break-all text-xs text-indigo-600">
              https://company.com/?krea-loan-app-id={"{"}loan_application_id
              {"}"}
            </code>
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-100 p-3">
            <p className="mb-1 text-helper">View application in your system:</p>
            <code className="block break-all text-xs text-indigo-600">
              https://company.com/krea/?lender-app-id={"{"}
              lender_application_id{"}"}
            </code>
          </div>
        </div>

        {/* Requirements */}
        <div className="space-y-2">
          <h3 className="text-base-strong">Requirements</h3>
          <ul className="list-disc space-y-1 pl-5 text-base">
            <li>
              Must be a valid URL where template tags <code>{"{}"}</code> are
              allowed.
            </li>
            <li>
              Can, but doesn&apos;t have to, contain variables defined in{" "}
              <code>{"{variable_name}"}</code>.
            </li>
          </ul>
        </div>

        {/* Valid variables table */}
        <div className="mt-2">
          <h3 className="mb-3 text-base-strong">Valid variable names</h3>

          <div className="overflow-hidden rounded-lg bg-black text-sm">
            {/* typography: dark-theme docs panel — gray-500 chosen for contrast on bg-black */}
            <div className="flex items-center justify-between border-b border-gray-600 p-4">
              <span className="text-xs text-gray-500">VARIABLE</span>
              <span className="text-xs text-gray-500">DESCRIPTION</span>
            </div>

            <div className="divide-y divide-gray-600">
              {VALID_VARIABLES.map((row) => (
                <div
                  key={row.variable}
                  className="flex flex-col gap-4 p-4 sm:flex-row"
                >
                  <code className="shrink-0 text-white sm:w-1/3">
                    {row.variable}
                  </code>
                  <p className="text-xs text-yellow-400/90 sm:text-sm">
                    {row.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-6" />
      <div className="flex justify-end">
        <Button
          size="lg"
          onClick={handleSubmit}
          disabled={
            form.formState.isSubmitting ||
            !form.formState.isValid ||
            !form.formState.isDirty
          }
        >
          Save
        </Button>
      </div>
    </LenderProfileSectionCard>
  );
};
